import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface DrillCluster {
  cluster_id: number;
  name: string;
  country: string;
  centroid_lon: number;
  centroid_lat: number;
  hole_count: number;
  total_meters: number | null;
  year_min: number | null;
  year_max: number | null;
  organizations: string;
  surface_rocks: string;
  pct_open_ground: number;
  pct_permitted: number;
  pct_natura2000: number;
  expired_holders: string | null;
  expired_minerals: string | null;
  fodd_classification: string;
  nearest_fodd_deposit: string | null;
  nearest_fodd_commodity: string | null;
  nearest_fodd_distance_km: number | null;
  nearest_fodd_status: string | null;
  geochem_score: number;
  geochem_elements_anomalous: string | null;
  au_till_max_ppb: number | null;
  cu_rock_max_ppm: number | null;
  ni_rock_max_ppm: number | null;
  lithology: string | null;
  metallogenic_domain: string | null;
  multi_metallic_potential: string | null;
  fault_count_5km: number;
  lineament_density: number | null;
  review_status: string;
  review_notes: string | null;
  reviewed_at: string | null;
  verdict: string | null;
  staking_polygon_geojson?: string | null;
  review_history?: Array<{
    id: number;
    verdict: string;
    notes: string | null;
    reviewed_at: string;
  }>;
}

export interface ClusterStats {
  total: number;
  reviewed: number;
  pending: number;
  by_status: Record<string, number>;
  by_fodd_classification: Record<string, number>;
  by_domain: Record<string, number>;
  by_verdict: Record<string, number>;
}

export interface ClusterFilters {
  status: string | null;
  foddClass: string | null;
  minHoles: number | null;
  openGroundOnly: boolean;
  minGeochem: number | null;
  verdict: string | null;
  domain: string | null;
}

interface DrillClusterState {
  clusters: DrillCluster[];
  selectedClusterId: number | null;
  selectedCluster: DrillCluster | null;
  stats: ClusterStats | null;
  filters: ClusterFilters;
  country: string;
  loading: boolean;
  error: string | null;
  panelOpen: boolean;
}

const initialState: DrillClusterState = {
  clusters: [],
  selectedClusterId: null,
  selectedCluster: null,
  stats: null,
  filters: {
    status: null,
    foddClass: null,
    minHoles: null,
    openGroundOnly: false,
    minGeochem: null,
    verdict: null,
    domain: null,
  },
  country: 'finland',
  loading: false,
  error: null,
  panelOpen: false,
};

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

export const fetchClusterStats = createAsyncThunk(
  'drillClusters/fetchStats',
  async (country: string) => {
    const res = await fetch(`${API_BASE}/api/drill-clusters/stats?country=${country}`);
    if (!res.ok) throw new Error(`Failed to fetch stats: ${res.status}`);
    return (await res.json()) as ClusterStats;
  }
);

export const fetchClusters = createAsyncThunk(
  'drillClusters/fetchClusters',
  async ({ country, filters }: { country: string; filters: ClusterFilters }) => {
    const params = new URLSearchParams();
    params.set('country', country);
    if (filters.status) params.set('status', filters.status);
    if (filters.foddClass) params.set('fodd_class', filters.foddClass);
    if (filters.minHoles) params.set('min_holes', String(filters.minHoles));
    if (filters.openGroundOnly) params.set('open_ground_only', 'true');
    if (filters.minGeochem) params.set('min_geochem', String(filters.minGeochem));
    if (filters.verdict) params.set('verdict', filters.verdict);
    if (filters.domain) params.set('domain', filters.domain);
    params.set('limit', '5000');

    const res = await fetch(`${API_BASE}/api/drill-clusters/?${params.toString()}`);
    if (!res.ok) throw new Error(`Failed to fetch clusters: ${res.status}`);
    return (await res.json()) as DrillCluster[];
  }
);

export const fetchClusterDetail = createAsyncThunk(
  'drillClusters/fetchDetail',
  async ({ clusterId, country }: { clusterId: number; country: string }, { signal }) => {
    const res = await fetch(`${API_BASE}/api/drill-clusters/${clusterId}?country=${country}`, { signal });
    if (!res.ok) throw new Error(`Failed to fetch cluster ${clusterId}: ${res.status}`);
    return (await res.json()) as DrillCluster;
  }
);

export const submitReview = createAsyncThunk(
  'drillClusters/submitReview',
  async ({ clusterId, country, verdict, notes }: { clusterId: number; country: string; verdict: string; notes?: string }) => {
    const res = await fetch(`${API_BASE}/api/drill-clusters/${clusterId}/review?country=${country}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verdict, notes }),
    });
    if (!res.ok) throw new Error(`Failed to submit review: ${res.status}`);
    return await res.json();
  }
);

const drillClusterSlice = createSlice({
  name: 'drillClusters',
  initialState,
  reducers: {
    setSelectedClusterId: (state, action: PayloadAction<number | null>) => {
      const nextId = action.payload;
      if (nextId !== state.selectedClusterId) {
        state.selectedCluster = null;
      }
      state.selectedClusterId = nextId;
      if (nextId === null) {
        state.selectedCluster = null;
        state.panelOpen = false;
      } else {
        state.panelOpen = true;
      }
    },
    setPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.panelOpen = action.payload;
    },
    setFilter: (state, action: PayloadAction<Partial<ClusterFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setCountry: (state, action: PayloadAction<string>) => {
      state.country = action.payload;
      state.selectedClusterId = null;
      state.selectedCluster = null;
      state.clusters = [];
      state.stats = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch stats
      .addCase(fetchClusterStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      // Fetch clusters
      .addCase(fetchClusters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClusters.fulfilled, (state, action) => {
        state.clusters = action.payload;
        state.loading = false;
      })
      .addCase(fetchClusters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch clusters';
      })
      // Fetch detail
      .addCase(fetchClusterDetail.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchClusterDetail.fulfilled, (state, action) => {
        // Ignore stale responses from older requests when selection changed quickly
        if (state.selectedClusterId === action.meta.arg.clusterId) {
          state.selectedCluster = action.payload;
        }
      })
      .addCase(fetchClusterDetail.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        state.error = action.error.message || 'Failed to fetch cluster detail';
      })
      // Submit review
      .addCase(submitReview.fulfilled, (state, action) => {
        const { cluster_id, verdict, review_status } = action.payload;
        // Update in list
        const idx = state.clusters.findIndex((c) => c.cluster_id === cluster_id);
        if (idx >= 0) {
          state.clusters[idx].verdict = verdict;
          state.clusters[idx].review_status = review_status;
        }
        // Update selected
        if (state.selectedCluster && state.selectedCluster.cluster_id === cluster_id) {
          state.selectedCluster.verdict = verdict;
          state.selectedCluster.review_status = review_status;
        }
        // Update stats
        if (state.stats) {
          state.stats.pending = Math.max(0, state.stats.pending - 1);
          state.stats.reviewed += 1;
        }
      });
  },
});

export const { setSelectedClusterId, setPanelOpen, setFilter, clearFilters, setCountry } = drillClusterSlice.actions;
export default drillClusterSlice.reducer;
